import { Controller } from 'react-hook-form';
import {
  CapitalActionFormState,
  CapitalActionFormValues,
} from '../../hooks/useCapitalActionForm';
import ActionSelector from './ActionSelector';

import { Grid } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import useDefaultEntryValues from '../../hooks/useDefaultEntryValues';
import ActionsDetails from './ActionsDetails';
import CapitalDistributionDetails from './CapitalDistributionDetails';
import CapitalRefundDetails from './CapitalRefundDetails';
import GeneralCapitalCallDetails from './GeneralCapitalCallDetails';
import useAvailableTransactionTypeOptions from './hooks/useAvailableTransactionTypeOptions';
import ShareCapitalCallDetails from './ShareCapitalCallDetails';

type Props = {
  hasUncalledEqualisations: boolean;
  index: number;
  vehicleId: string;
  baseCurrency: CurrencyCode;
  hasDistributionEntry: boolean;
  entriesCount: number;
};

const CapitalActionEntry: React.FC<Props> = ({
  hasUncalledEqualisations,
  index,
  vehicleId,
  baseCurrency,
  hasDistributionEntry,
  entriesCount,
}) => {
  const { defaultEntryValues } = useDefaultEntryValues();

  const {
    formMethods: { control, watch, setValue, getValues },
    state: { isEditForm },
  } = useFormWithStateContext<
    CapitalActionFormValues,
    CapitalActionFormState
  >();

  const entryId = watch(`entries.${index}.id`);
  const entryType = watch(`entries.${index}.entryType`);
  const actions = watch(`entries.${index}.actions`);

  const date = getValues('ledgerDate');

  const disableChanges = isEditForm && !!entryId;

  const availableTransactionTypes = useAvailableTransactionTypeOptions({
    currentEntryType: entryType,
    currentEntryIndex: index,
    entriesCount,
    hasDistributionEntry,
    hasUncalledEqualisations,
  });

  return (
    <Column spacing="lg">
      <Grid container>
        <Grid size={{ sm: 12, md: 12, lg: 8 }}>
          <FormField label="Select transaction type" size="md">
            <Controller
              name={`entries.${index}.entryType`}
              control={control}
              render={({
                fieldState: { error },
                field: { onChange, ...rest },
              }) => (
                <BasicSelect
                  fullWidth
                  options={availableTransactionTypes}
                  placeholder="Select transaction type"
                  error={!!error}
                  disabled={disableChanges}
                  onChange={(value) => {
                    setValue(`entries.${index}`, defaultEntryValues[value]);
                    onChange(value);
                  }}
                  {...rest}
                />
              )}
            />
          </FormField>
        </Grid>
      </Grid>

      <ErrorBoundary>
        {entryType === CapitalActionEntryType.GeneralCapitalCall ? (
          <GeneralCapitalCallDetails
            index={index}
            vehicleId={vehicleId}
            entryType={entryType}
            baseCurrency={baseCurrency}
          />
        ) : entryType === CapitalActionEntryType.CapitalContributionCall ? (
          <ShareCapitalCallDetails
            index={index}
            vehicleId={vehicleId}
            baseCurrency={baseCurrency}
            actionId={entryId}
          />
        ) : entryType === CapitalActionEntryType.CapitalRefund ? (
          <CapitalRefundDetails
            index={index}
            vehicleId={vehicleId}
            entryType={entryType}
            baseCurrency={baseCurrency}
          />
        ) : entryType === CapitalActionEntryType.CapitalDistribution ? (
          <CapitalDistributionDetails
            index={index}
            vehicleId={vehicleId}
            entryType={entryType}
            baseCurrency={baseCurrency}
            ledgerDate={date}
          />
        ) : (
          <Grid container>
            <Grid size={{ sm: 12, md: 12, lg: 8 }}>
              <ActionSelector
                disabled={disableChanges}
                index={index}
                vehicleId={vehicleId}
                entryType={entryType}
                baseCurrency={baseCurrency}
              />
            </Grid>
          </Grid>
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        {!!actions.length && (
          <ActionsDetails
            vehicleId={vehicleId}
            entryType={entryType}
            actions={actions}
            entryIndex={index}
          />
        )}
      </ErrorBoundary>
    </Column>
  );
};

export default CapitalActionEntry;
